import { pdfjs } from "react-pdf";
import { getFileType } from "./helper";

// Configure PDF.js worker for standalone usage
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Render PDF pages to an array of high-quality base64 image strings
 * @param {string} pdfUrl - URL of the PDF to render
 * @returns {Promise<string[]>} Array of base64 image URLs
 */
const renderPdfToImageUrls = async (pdfUrl) => {
  if (!pdfUrl) return [];
  try {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdf = await loadingTask.promise;
    const imageUrls = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      // Scale 2.0 provides good print quality balancing memory usage
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport }).promise;
      imageUrls.push(canvas.toDataURL("image/png"));

      // Clean up to prevent memory leaks during multi-page rendering
      page.cleanup();
    }
    return imageUrls;
  } catch (err) {
    console.warn("Error rendering PDF to images:", pdfUrl, err);
    return [];
  }
};

/**
 * Wait for image prefetching to complete
 * @param {boolean} isPrefetching - Current prefetching state
 * @param {number} maxWaitMs - Maximum time to wait in milliseconds
 * @returns {Promise<void>}
 */
const waitForPrefetch = async (isPrefetching, maxWaitMs = 5000) => {
  if (!isPrefetching) return;

  const start = Date.now();
  while (isPrefetching && Date.now() - start < maxWaitMs) {
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
};

/**
 * Helper to render file content based on type (PDF or Image)
 * @param {string|string[]} content - The actual URL(s) to use for rendering. Can be an array if PDF pages were pre-rendered.
 * @param {string} typeUrl - The original URL used for type detection (has extension)
 * @param {string} altText - Alt text for images
 * @param {string} placeholder - Placeholder image URL
 */
const renderFile = (content, typeUrl, altText, placeholder) => {
  const isPreRenderedPdf = Array.isArray(content);
  const fileType = getFileType(
    typeUrl || (typeof content === "string" ? content : ""),
  );

  if (isPreRenderedPdf || fileType === "pdf") {
    const images = isPreRenderedPdf ? content : [content];

    if (images.length === 0) {
      return `<img src="${placeholder}" alt="No attachment" />`;
    }

    return `
      <div class="pdf-image-container">
        ${images
          .map(
            (src, idx) => `
          <img 
            src="${src}" 
            alt="${altText} - Page ${idx + 1}" 
            class="pdf-page-image"
            onerror="this.onerror=null;this.src='${placeholder}'" 
          />
        `,
          )
          .join("")}
      </div>
    `;
  }

  return `<img src="${content}" alt="${altText}" onerror="this.onerror=null;this.src='${placeholder}'" />`;
};

/**
 * Generate HTML for a single question section
 * @param {Object} question - Question object
 * @param {Object} prefetchedData - Prefetched image/blob URLs
 * @returns {Promise<string>} HTML string
 */
const generateQuestionHTML = async (question, prefetchedData = {}) => {
  const questionFileUrl =
    prefetchedData.question_file || question.question_file;
  const msFileUrl =
    prefetchedData.ms_file ||
    question.ms_file ||
    "https://placehold.co/600x400?text=No+attachment";
  const placeholderImg = "https://placehold.co/600x400?text=No+attachment";

  const processedQuestionFile =
    getFileType(question.question_file) === "pdf"
      ? await renderPdfToImageUrls(questionFileUrl)
      : questionFileUrl;

  const processedMsFile =
    getFileType(question.ms_file) === "pdf"
      ? await renderPdfToImageUrls(msFileUrl)
      : msFileUrl;

  const solutionFileUrl =
    prefetchedData.solution_file || question.solution_file;

  const processedSolutionFile =
    getFileType(question.solution_file) === "pdf"
      ? await renderPdfToImageUrls(solutionFileUrl)
      : solutionFileUrl;

  const topicsText = question?.topics?.map((t) => t.title).join(", ") || "N/A";
  const subtopicsText =
    question?.subtopics?.map((st) => st.title).join(", ") || "N/A";
  const chapterText =
    question?.chapter?.map((st) => st.title).join(", ") || "N/A";
  const sectionText =
    question?.section?.map((st) => st.title).join(", ") || "N/A";

  const questionLabel = [
    "Q.",
    question?.question_number,
    question?.part,
    question?.subpart && `(${question?.subpart})`,
  ]
    .filter(Boolean)
    .join(" ");

  let html = `
    <!-- Question Section -->
    <div class="print-section question-section">
      <h4 style="margin-top: 0">
        Question: ${questionLabel}
      </h4>
      <p style="margin: 0">
        <strong>Paper:</strong> ${question?.paper || "N/A"}, 
        <strong>Year:</strong> ${question?.year || "N/A"}, 
      </p>
      <p style="margin: 0">
        <strong>Topics:</strong> ${topicsText}
      </p>
      <p style="margin: 0">
        <strong>Subtopics:</strong> ${subtopicsText}
      </p>
      <p style="margin: 0">
        <strong>Chapter:</strong> ${chapterText}
      </p>
      <p style="margin: 0">
        <strong>Section:</strong> ${sectionText}
      </p>
      <p style="margin: 0">
        <strong>Marks:</strong> ${question?.marks || "N/A"}
      </p>
      ${renderFile(processedQuestionFile, question.question_file, "Question", placeholderImg)}
    </div>

    <!-- Marking Scheme Section -->
    <div class="print-section marking-scheme-section">
      <h4 style="margin-top: 0">
        Marking Scheme for ${questionLabel}
      </h4>
      ${renderFile(processedMsFile, question.ms_file, "Marking Scheme", placeholderImg)}
    </div>

    <!-- Solution Section -->
    ${
      processedSolutionFile
        ? `
    <div class="print-section solution-section">
      <h4 style="margin-top: 0">
        Solution for ${questionLabel}
      </h4>
      ${renderFile(processedSolutionFile, question.solution_file, "Solution", placeholderImg)}
    </div>
    `
        : ""
    }
  `;

  // Add dependencies if they exist
  if (question.dependencies?.length > 0) {
    for (let idx = 0; idx < question.dependencies.length; idx++) {
      const dep = question.dependencies[idx];
      const depUrl = prefetchedData.deps?.[dep._id] || dep.file_name || "";

      const processedDepFile =
        getFileType(dep.file_name) === "pdf"
          ? await renderPdfToImageUrls(depUrl)
          : depUrl;

      html += `
        <div class="print-section dependency-section">
          <h4 style="margin-top: 0">
            Dependency ${idx + 1} for Q. ${question?.question_number || ""}
          </h4>
          ${renderFile(processedDepFile, dep.file_name, `Dependency ${idx + 1}`, placeholderImg)}
        </div>
      `;
    }
  }

  return html;
};

/**
 * Generate complete print HTML document
 * @param {string} contentHTML - The main content HTML
 * @param {string} title - Document title
 * @returns {string} Complete HTML document
 */
const generatePrintDocument = (contentHTML, title = "Question Details") => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        /* Set standard print page size and margins */
        @page {
          size: A4;
          margin: 0.5in;
        }
        * {
          box-sizing: border-box !important;
        }
        html, body {
          margin: 0;
          padding: 0;
          width: 100% !important;
          overflow-x: hidden !important;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: #333;
          line-height: 1.6;
        }
        .print-container {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 auto;
          padding: 0;
        }
        .print-section {
          width: 100% !important;
          margin-bottom: 2.5rem;
          page-break-inside: auto;
          break-inside: auto;
          padding-top: 0.5rem;
          overflow: hidden;
        }
        .print-section:not(:last-child) {
          page-break-after: auto;
        }
        h4 {
          font-size: 1.3rem;
          color: #7a5d10;
          border-bottom: 1px solid #eee;
          padding-bottom: 0.5rem;
          margin-top: 0;
          margin-bottom: 1rem;
          page-break-after: avoid;
          width: 100%;
        }
        p {
          margin: 0.2rem 0;
          page-break-inside: avoid;
        }
        strong {
          font-weight: 600;
        }
        img, .pdf-image-container {
          max-width: 100% !important;
          width: 100% !important;
          height: auto;
          display: block;
          margin: 1.5rem 0 2rem 0;
          border: 1px solid #dee2e6;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          page-break-inside: auto;
          overflow: hidden;
        }
        .pdf-page-image {
          display: block;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          margin-bottom: 10px;
        }
        .pdf-page-image:last-child {
          margin-bottom: 0;
        }
        /* Separator between multiple questions */
        .question-separator {
          border-top: 3px solid #7a5d10;
          margin: 3rem 0;
          page-break-before: always;
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        ${contentHTML}
      </div>
      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
          }, 500);
        }
      </script>
    </body>
    </html>
  `;
};

/**
 * Print single question to PDF
 * @param {Object} question - Question object
 * @param {Object} prefetchedImages - Prefetched image URLs
 * @param {boolean} isPrefetching - Whether images are still being prefetched
 * @returns {Promise<void>}
 */
export const printSingleQuestion = async (
  question,
  prefetchedImages = {},
  isPrefetching = false,
) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  if (!question) {
    console.error("No question data provided for printing.");
    return;
  }

  const contentHTML = await generateQuestionHTML(question, prefetchedImages);
  const title = `Question Details: Q. ${question?.question_number || ""}`;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};

/**
 * Print multiple questions to PDF
 * @param {Array<Object>} questions - Array of question objects
 * @param {Array<Object>} prefetchedImagesArray - Array of prefetched image objects (one per question)
 * @param {boolean} isPrefetching - Whether images are still being prefetched
 * @returns {Promise<void>}
 */
export const printMultipleQuestions = async (
  questions,
  prefetchedImagesArray = [],
  isPrefetching = false,
) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  if (!questions || questions.length === 0) {
    console.error("No questions provided for printing.");
    return;
  }

  let contentHTML = "";

  for (let index = 0; index < questions.length; index++) {
    const question = questions[index];
    const prefetchedImages = prefetchedImagesArray[index] || {};

    // Add separator between questions (except before first question)
    if (index > 0) {
      contentHTML += '<div class="question-separator"></div>';
    }

    contentHTML += await generateQuestionHTML(question, prefetchedImages);
  }

  const title = `Question Details: ${questions.length} Question${questions.length > 1 ? "s" : ""}`;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};

/**
 * Open a new window with print content
 * @param {string} printHTML - Complete HTML document to print
 */
const openPrintWindow = (printHTML) => {
  const printWindow = window.open("", "_blank", "width=800,height=600");

  if (!printWindow) {
    console.error(
      "Could not open new window for printing. Check pop-up blockers.",
    );
    alert(
      "Unable to open print window. Please check your pop-up blocker settings.",
    );
    return;
  }

  printWindow.document.write(printHTML);
  printWindow.document.close();
};

/**
 * Helper function to extract content from DOM element for printing
 * This is useful when you have a hidden print section in your component
 * @param {string} selector - CSS selector for the print content wrapper
 * @param {string} title - Document title
 * @returns {void}
 */
export const printFromDOMElement = async (
  selector,
  title = "Print Document",
  isPrefetching = false,
) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  const printContentElement = document.querySelector(selector);

  if (!printContentElement) {
    console.error(`Print content element not found: ${selector}`);
    return;
  }

  const contentHTML = printContentElement.innerHTML;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};
